// ============================================
// Vuex 状态管理
// ============================================
// 参考 RuoYi-UI 认证流程重构

import Vue from 'vue'
import Vuex from 'vuex'
import { login, getInfo, getRouters } from '../api/auth'
import { setToken, getToken, clearAuth } from '../utils/auth'
import { constantRoutes } from '../router'
import Layout from '@/layout/Layout.vue'

Vue.use(Vuex)

// ============================================
// 菜单转换函数
// ============================================
/**
 * 将路由数据转换为 Sidebar 需要的菜单格式
 * @param routerData 后台返回的路由数据
 */
function convertToMenus(routerData) {
  return routerData.map(route => ({
    id: route.name,
    name: route.meta?.title || route.name,
    path: route.path,
    icon: route.meta?.icon,
    children: route.children ? convertToMenus(route.children) : []
  }))
}

// ============================================
// 路由转换函数
// ============================================
/**
 * 遍历后台传来的路由字符串，转换为组件对象
 * @param asyncRouterMap 后台返回的路由表
 */
function filterAsyncRouter(asyncRouterMap) {
  return asyncRouterMap.filter(route => {
    // 处理 component 属性
    if (route.component) {
      if (route.component === 'Layout') {
        route.component = Layout
      } else {
        // 动态加载组件 (system/User -> views/system/User.vue)
        const componentPath = route.component
        route.component = resolve => require([`@/views/${componentPath}.vue`], resolve)
      }
    } else {
      // 如果没有 component 且有子元素，通常是目录，使用 Layout
      if (route.children && route.children.length) {
        route.component = Layout
      }
    }

    // 递归处理子路由
    if (route.children && route.children.length) {
      route.children = filterAsyncRouter(route.children)
    }
    return true
  })
}


// ============================================
// Vuex Store 定义
// ============================================
export default new Vuex.Store({
  state: {
    token: getToken(),
    user: null,              // 不从 localStorage 读取
    roles: [],               // 角色数组（用于判断是否已加载）
    permissions: [],         // 权限标识数组（用于按钮权限）
    menus: [],               // 菜单数据（用于侧边栏渲染）
    routes: [],              // 完整路由表
    addRoutes: [],           // 动态添加的路由
    sidebarCollapsed: false,
    visitedViews: [],
    cachedViews: []
  },

  mutations: {
    SET_TOKEN(state, token) {
      state.token = token
    },
    SET_USER(state, user) {
      state.user = user
    },
    SET_ROLES(state, roles) {
      state.roles = roles
    },
    SET_PERMISSIONS(state, permissions) {
      state.permissions = permissions
    },
    SET_MENUS(state, menus) {
      state.menus = menus
    },
    SET_ROUTES(state, routes) {
      state.addRoutes = routes
      state.routes = constantRoutes.concat(routes)
    },
    TOGGLE_SIDEBAR(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed
    },
    // TagsView
    ADD_VISITED_VIEW(state, view) {
      if (state.visitedViews.some(v => v.path === view.path)) return
      state.visitedViews.push({
        name: view.name,
        path: view.path,
        title: view.meta?.title || 'no-name',
        query: view.query,
        fullPath: view.fullPath
      })
    },
    ADD_CACHED_VIEW(state, view) {
      if (state.cachedViews.includes(view.name)) return
      if (view.meta?.keepAlive !== false) {
        state.cachedViews.push(view.name)
      }
    },
    DEL_VISITED_VIEW(state, view) {
      const index = state.visitedViews.findIndex(v => v.path === view.path)
      if (index > -1) state.visitedViews.splice(index, 1)
    },
    DEL_CACHED_VIEW(state, view) {
      const index = state.cachedViews.indexOf(view.name)
      if (index > -1) state.cachedViews.splice(index, 1)
    },
    DEL_OTHER_VISITED_VIEWS(state, view) {
      state.visitedViews = state.visitedViews.filter(v => v.path === view.path || v.meta?.affix)
    },
    DEL_OTHER_CACHED_VIEWS(state, view) {
      const index = state.cachedViews.indexOf(view.name)
      state.cachedViews = index > -1 ? state.cachedViews.slice(index, index + 1) : []
    },
    DEL_ALL_VISITED_VIEWS(state) {
      state.visitedViews = state.visitedViews.filter(v => v.meta?.affix)
    },
    DEL_ALL_CACHED_VIEWS(state) {
      state.cachedViews = []
    },
    CLEAR_AUTH(state) {
      state.token = null
      state.user = null
      state.roles = []
      state.permissions = []
      state.menus = []
      state.routes = []
      state.addRoutes = []
    }
  },

  actions: {
    // 登录（只存储 Token）
    async login({ commit }, loginForm) {
      const res = await login(loginForm)
      const { token } = res.data
      setToken(token)
      commit('SET_TOKEN', token)
      return res
    },

    // 获取用户信息、角色、权限
    async getInfo({ commit }) {
      const res = await getInfo()
      const { user, roles, permissions } = res.data
      
      // 存储到 Vuex state（不存储到 localStorage）
      commit('SET_USER', user)
      commit('SET_ROLES', roles || [])
      commit('SET_PERMISSIONS', permissions || [])
      
      return res
    },

    // 生成路由
    async generateRoutes({ commit }) {
      const res = await getRouters()
      const routerData = res.data
      
      // 保存原始菜单数据（用于侧边栏渲染）
      // 转换为 Sidebar 需要的格式
      const menus = convertToMenus(routerData)
      commit('SET_MENUS', menus)
      
      // 深拷贝防止直接修改
      const s_routes = JSON.parse(JSON.stringify(routerData))
      const accessedRoutes = filterAsyncRouter(s_routes)
      
      // 404 路由需要在最后添加
      accessedRoutes.push({ path: '*', redirect: '/404', meta: { hidden: true } })

      commit('SET_ROUTES', accessedRoutes)
      return accessedRoutes
    },

    // 登出
    logout({ commit }) {
      clearAuth()
      commit('CLEAR_AUTH')
      commit('DEL_ALL_VISITED_VIEWS')
      commit('DEL_ALL_CACHED_VIEWS')
    },

    // TagsView actions
    addView({ commit }, view) {
      commit('ADD_VISITED_VIEW', view)
      commit('ADD_CACHED_VIEW', view)
    },
    delView({ commit, state }, view) {
      commit('DEL_VISITED_VIEW', view)
      commit('DEL_CACHED_VIEW', view)
      return [...state.visitedViews]
    },
    delOtherViews({ commit }, view) {
      commit('DEL_OTHER_VISITED_VIEWS', view)
      commit('DEL_OTHER_CACHED_VIEWS', view)
    },
    delAllViews({ commit }) {
      commit('DEL_ALL_VISITED_VIEWS')
      commit('DEL_ALL_CACHED_VIEWS')
    }
  },

  getters: {
    token: state => state.token,
    user: state => state.user,
    roles: state => state.roles,
    permissions: state => state.permissions,
    menus: state => state.menus,
    permission_routes: state => state.routes,
    sidebarCollapsed: state => state.sidebarCollapsed,
    visitedViews: state => state.visitedViews,
    cachedViews: state => state.cachedViews,
    hasPermission: state => permission => {
      if (!permission) return true
      // 超级管理员权限
      if (state.permissions.includes('*:*:*')) return true
      return state.permissions.includes(permission)
    }
  }
})
